export const CAMPAIGN_GOAL_USD = 1_000_000
export const ROAD_PIXEL_PRICE_USD = 1_000

export type ZoneStatus = 'available' | 'reserved' | 'installed'

export interface Zone {
  readonly id: string
  readonly number: number
  readonly status: ZoneStatus
  readonly roadPixelCount: number
  readonly priceUsd: number
  readonly vehicleLocation: string
  readonly sponsorId?: string
}

export interface Sponsor {
  readonly id: string
  readonly name: string
  readonly zoneId: string
  /** Net settled value after any refunds or chargebacks. */
  readonly paidAmountUsd: number
  readonly approvedDescription: string
  readonly destinationUrl?: string
  readonly installedMonthYear: string
  readonly proofVerified: boolean
}

export type LedgerEntryKind =
  'settled-payment' | 'refund' | 'chargeback' | 'correction'

export interface CampaignLedgerEntry {
  readonly id: string
  readonly sponsorId: string
  readonly zoneId: string
  readonly kind: LedgerEntryKind
  /** Signed whole-dollar amount. Refunds, chargebacks, and corrections are negative. */
  readonly amountUsd: number
  readonly effectiveDate: string
  readonly note?: string
}

export interface CampaignLedger {
  readonly goalUsd: number
  readonly entries: readonly CampaignLedgerEntry[]
}

export interface StoryEpisode {
  readonly id: string
  readonly episodeNumber: number
  readonly title: string
  readonly body: string
  readonly watchUrl?: string
}

export interface CampaignFixture {
  readonly zones: readonly Zone[]
  readonly sponsors: readonly Sponsor[]
  readonly ledger: CampaignLedger
  readonly storyEpisodes: readonly StoryEpisode[]
}

export function derivePaidTotal(ledger: CampaignLedger): number {
  return ledger.entries.reduce((total, entry) => total + entry.amountUsd, 0)
}

export function deriveRemaining(ledger: CampaignLedger): number {
  return Math.max(ledger.goalUsd - derivePaidTotal(ledger), 0)
}

export function deriveCompletionPercentage(ledger: CampaignLedger): number {
  if (ledger.goalUsd <= 0) return 0

  const percentage = (derivePaidTotal(ledger) / ledger.goalUsd) * 100
  return Number(Math.min(Math.max(percentage, 0), 100).toFixed(2))
}

export function deriveSponsorPaidTotal(
  sponsorId: string,
  ledger: CampaignLedger,
): number {
  return ledger.entries.reduce(
    (total, entry) =>
      entry.sponsorId === sponsorId ? total + entry.amountUsd : total,
    0,
  )
}

export function deriveFrontRunner(
  sponsors: readonly Sponsor[],
  ledger: CampaignLedger,
): Sponsor | undefined {
  return sponsors.reduce<Sponsor | undefined>((leader, sponsor) => {
    if (!leader) return sponsor

    const sponsorPaid = deriveSponsorPaidTotal(sponsor.id, ledger)
    const leaderPaid = deriveSponsorPaidTotal(leader.id, ledger)

    if (sponsorPaid !== leaderPaid)
      return sponsorPaid > leaderPaid ? sponsor : leader

    return sponsor.name.localeCompare(leader.name) < 0 ? sponsor : leader
  }, undefined)
}

export function deriveAvailableZones(zones: readonly Zone[]): readonly Zone[] {
  return zones
    .filter((zone) => zone.status === 'available')
    .sort((left, right) => left.number - right.number)
}

function isValidHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && Boolean(url.hostname)
  } catch {
    return false
  }
}

function hasDuplicates(values: readonly (string | number)[]): boolean {
  return new Set(values).size !== values.length
}

export function validateCampaignInvariants(
  campaign: CampaignFixture,
): readonly string[] {
  const issues: string[] = []
  const zoneById = new Map(campaign.zones.map((zone) => [zone.id, zone]))
  const sponsorById = new Map(
    campaign.sponsors.map((sponsor) => [sponsor.id, sponsor]),
  )

  if (campaign.ledger.goalUsd !== CAMPAIGN_GOAL_USD) {
    issues.push(
      `Campaign goal must be $${CAMPAIGN_GOAL_USD.toLocaleString('en-US')}.`,
    )
  }

  if (campaign.zones.length !== 10) {
    issues.push('Fixture campaign must contain exactly 10 zones.')
  }

  if (hasDuplicates(campaign.zones.map((zone) => zone.id))) {
    issues.push('Zone IDs must be unique.')
  }

  if (hasDuplicates(campaign.zones.map((zone) => zone.number))) {
    issues.push('Zone numbers must be unique.')
  }

  if (hasDuplicates(campaign.sponsors.map((sponsor) => sponsor.id))) {
    issues.push('Sponsor IDs must be unique.')
  }

  if (hasDuplicates(campaign.ledger.entries.map((entry) => entry.id))) {
    issues.push('Ledger entry IDs must be unique.')
  }

  const roadPixelTotal = campaign.zones.reduce(
    (total, zone) => total + zone.roadPixelCount,
    0,
  )
  const inventoryValueUsd = campaign.zones.reduce(
    (total, zone) => total + zone.priceUsd,
    0,
  )

  if (roadPixelTotal !== 1_000) {
    issues.push('Zone inventory must total exactly 1,000 road pixels.')
  }

  if (inventoryValueUsd !== CAMPAIGN_GOAL_USD) {
    issues.push('Zone inventory must total exactly $1,000,000.')
  }

  for (const zone of campaign.zones) {
    if (!Number.isInteger(zone.roadPixelCount) || zone.roadPixelCount <= 0) {
      issues.push(
        `${zone.id} must contain a positive whole number of road pixels.`,
      )
    }

    if (zone.priceUsd !== zone.roadPixelCount * ROAD_PIXEL_PRICE_USD) {
      issues.push(
        `${zone.id} price must equal its road-pixel count times $1,000.`,
      )
    }

    if (zone.status === 'installed' && !zone.sponsorId) {
      issues.push(`${zone.id} is installed but has no sponsor.`)
    }

    if (zone.status !== 'installed' && zone.sponsorId) {
      issues.push(`${zone.id} is not installed and cannot have a sponsor.`)
    }
  }

  for (const sponsor of campaign.sponsors) {
    const zone = zoneById.get(sponsor.zoneId)
    if (!zone || zone.status !== 'installed' || zone.sponsorId !== sponsor.id) {
      issues.push(`${sponsor.id} must reference its matching installed zone.`)
    }

    if (
      sponsor.destinationUrl !== undefined &&
      !isValidHttpsUrl(sponsor.destinationUrl)
    ) {
      issues.push(`${sponsor.id} destination must be a valid HTTPS URL.`)
    }

    const ledgerPaid = deriveSponsorPaidTotal(sponsor.id, campaign.ledger)
    if (ledgerPaid !== sponsor.paidAmountUsd) {
      issues.push(`${sponsor.id} paid amount must match its net ledger total.`)
    }
  }

  for (const entry of campaign.ledger.entries) {
    const zone = zoneById.get(entry.zoneId)
    const sponsor = sponsorById.get(entry.sponsorId)

    if (!zone || !sponsor || sponsor.zoneId !== entry.zoneId) {
      issues.push(`${entry.id} must reference a matching sponsor and zone.`)
    }

    if (!Number.isInteger(entry.amountUsd) || entry.amountUsd === 0) {
      issues.push(`${entry.id} must contain a non-zero whole-dollar amount.`)
    }

    const isPayment = entry.kind === 'settled-payment'
    if (
      (isPayment && entry.amountUsd < 0) ||
      (!isPayment && entry.amountUsd > 0)
    ) {
      issues.push(`${entry.id} amount sign does not match its ledger kind.`)
    }
  }

  for (const zone of campaign.zones.filter(
    (item) => item.status === 'installed',
  )) {
    const grossSettled = campaign.ledger.entries.reduce(
      (total, entry) =>
        entry.zoneId === zone.id && entry.kind === 'settled-payment'
          ? total + entry.amountUsd
          : total,
      0,
    )
    if (grossSettled !== zone.priceUsd) {
      issues.push(
        `${zone.id} gross settled payments must equal its inventory price.`,
      )
    }
  }

  const paidTotal = derivePaidTotal(campaign.ledger)
  if (paidTotal < 0 || paidTotal > campaign.ledger.goalUsd) {
    issues.push(
      'Net paid total must remain between zero and the campaign goal.',
    )
  }

  return issues
}

export function assertValidCampaign(campaign: CampaignFixture): void {
  const issues = validateCampaignInvariants(campaign)
  if (issues.length > 0) {
    throw new Error(`Invalid campaign fixture:\n- ${issues.join('\n- ')}`)
  }
}
